import { prefersReducedMotion } from "../utils/reduced-motion";

const PHASE_SCROLL_MIN = 40;
const PHASE_SCROLL_MAX = 60;
const LERP_FACTOR = 0.2;
const SETTLE_THRESHOLD = 0.25;
const OVERSCROLL_GROW_RATE = 0.0035;
const OVERSCROLL_MAX = 160;
const PULL_DECAY = 0.88;
const INTRO_HOLD_MS = 1500;
const INTRO_DURATION_MS = 1100;
/** Only snap when near fully open / fully closed; leave mid-phase alone. */
const PHASE_SNAP_OPEN_MAX = 0.30;
const PHASE_SNAP_CLOSE_MIN = 0.70;
const PHASE_SNAP_IDLE_MS = 150;
export const HOME_INTRO_DONE_EVENT = "modulo:home-intro-done";

/**
 * In-memory only — survives Swup soft navigations within the same page life,
 * but resets on full refresh so a direct homepage landing can play intro again.
 */
let homeIntroDoneThisPageLife = false;
let recordedInitialEntry = false;

/**
 * Intro plays only when the user first lands on the homepage in this page life.
 * Landing elsewhere (e.g. projects → home) skips intro for the rest of the session.
 */
function rememberInitialEntryPage() {
  if (recordedInitialEntry) {
    return;
  }

  recordedInitialEntry = true;

  if (!document.body.classList.contains("page-home")) {
    homeIntroDoneThisPageLife = true;
  }
}

/** Resting row heights — row 2 is 80/185 of row 1; row 3 uses the same step on mobile. */
const LOGO_ROW_1_BASE = 185;
const LOGO_ROW_2_BASE = 80;
const LOGO_ROW_STEP = LOGO_ROW_2_BASE / LOGO_ROW_1_BASE;
const LOGO_ROW_3_BASE = LOGO_ROW_2_BASE * LOGO_ROW_STEP;
const LOGO_ROWS_TOTAL_DESKTOP = LOGO_ROW_1_BASE + LOGO_ROW_2_BASE;
const LOGO_ROWS_TOTAL_MOBILE =
  LOGO_ROW_1_BASE + LOGO_ROW_2_BASE + LOGO_ROW_3_BASE;
const MOBILE_LOGO_MQ = "(max-width: 767px)";

let cachedLogoRowGap = null;

function getLogoRowGap() {
  if (cachedLogoRowGap === null) {
    cachedLogoRowGap =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--margin")
      ) || 10;
  }

  return cachedLogoRowGap;
}

function resetLogoRowGapCache() {
  cachedLogoRowGap = null;
}

function isMobileLogoLayout(logo) {
  return (
    window.matchMedia(MOBILE_LOGO_MQ).matches &&
    Boolean(logo?.querySelector(".c-ma-logo__row--tertiary"))
  );
}

function tertiaryHeightFromSecondary(secondaryHeight) {
  return secondaryHeight * LOGO_ROW_STEP;
}

function isHomeIntroDone() {
  if (!document.body.classList.contains("page-home")) {
    return true;
  }

  return (
    document.documentElement.classList.contains("ma-logo-intro-done") ||
    !document.documentElement.classList.contains("is-ma-logo-intro")
  );
}

/**
 * Resolves when the homepage logo intro has finished or was skipped.
 */
export function whenHomeIntroDone() {
  if (isHomeIntroDone()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    document.addEventListener(HOME_INTRO_DONE_EVENT, () => resolve(), { once: true });
  });
}

function hasHomeIntroBeenDone() {
  return homeIntroDoneThisPageLife;
}

function persistHomeIntroDone() {
  homeIntroDoneThisPageLife = true;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function heightAtProgress(max, progress) {
  return max * (1 - progress);
}

function smoothToward(current, target, factor) {
  return current + (target - current) * factor;
}

function phaseScrollRange(rowHeight) {
  return clamp(Math.round(rowHeight * 0.35), PHASE_SCROLL_MIN, PHASE_SCROLL_MAX);
}

/** @returns {'keep_first_ma' | 'full_collapse'} */
function getLogoStickyMode() {
  const mode = document.body?.dataset?.logoStickyMode;
  return mode === "full_collapse" ? "full_collapse" : "keep_first_ma";
}

function keepsFirstMa(mode = getLogoStickyMode()) {
  return mode === "keep_first_ma";
}

function overscrollGrowFactor(overscrollPx) {
  if (overscrollPx <= 0) {
    return 1;
  }

  return 1 + clamp(overscrollPx, 0, OVERSCROLL_MAX) * OVERSCROLL_GROW_RATE;
}

function applyOverscrollGrowth(state, heights, growFactor) {
  if (growFactor <= 1) {
    return state;
  }

  const next = {
    ...state,
    primaryHeight: heights.primaryMax * growFactor,
    secondaryHeight: heights.secondaryMax * growFactor,
    primaryProgress: 0,
    secondaryProgress: 0,
    scrollProgress: Math.min(0, state.scrollProgress),
  };

  if (heights.tertiaryMax > 0) {
    next.tertiaryHeight = heights.tertiaryMax * growFactor;
  }

  return next;
}

function gapForRowHeight(height, maxHeight, rowGap) {
  if (maxHeight <= 0 || height <= 0) {
    return 0;
  }

  // Keep the full gap while a row is still clearly visible; fade only at the end.
  const fadeThreshold = Math.max(8, maxHeight * 0.12);

  if (height >= fadeThreshold) {
    return rowGap;
  }

  return rowGap * clamp(height / fadeThreshold, 0, 1);
}

function mobileLogoGaps(
  primaryHeight,
  secondaryHeight,
  primaryMax,
  secondaryMax,
  rowGap
) {
  const gapAfterPrimary = gapForRowHeight(
    primaryHeight,
    primaryMax,
    rowGap
  );
  const gapAfterSecondary = gapForRowHeight(
    secondaryHeight,
    secondaryMax,
    rowGap
  );

  return {
    gapAfterPrimary,
    gapAfterSecondary,
    interRowGapsTotal: gapAfterPrimary + gapAfterSecondary,
  };
}

function desktopLogoGaps(
  primaryHeight,
  secondaryHeight,
  primaryMax,
  secondaryMax,
  rowGap,
  stickyMode = getLogoStickyMode()
) {
  if (keepsFirstMa(stickyMode)) {
    const interRowGap = gapForRowHeight(primaryHeight, primaryMax, rowGap);

    return {
      interRowGap,
      brandGap: rowGap,
    };
  }

  const interRowGap =
    primaryHeight > 0
      ? gapForRowHeight(primaryHeight, primaryMax, rowGap)
      : gapForRowHeight(secondaryHeight, secondaryMax, rowGap);

  return {
    interRowGap,
    brandGap: interRowGap,
  };
}

function withLogoMaxHeights(state, heights) {
  if (!heights) {
    return state;
  }

  return {
    ...state,
    logoMaxHeights: {
      primaryMax: heights.primaryMax,
      secondaryMax: heights.secondaryMax,
    },
    stickyMode: heights.stickyMode,
  };
}

/** Last applied logo CSS payload — skip identical writes (scroll jank). */
let lastLogoCssKey = "";

function logoCssKey(parts) {
  return parts.join("|");
}

function setMaLogoState({
  primaryHeight,
  secondaryHeight,
  tertiaryHeight,
  primaryProgress,
  secondaryProgress,
  scrollProgress,
  logoMaxHeights = null,
  stickyMode = getLogoStickyMode(),
}) {
  const root = document.documentElement;
  const rowGap = getLogoRowGap();
  const primaryPx = Math.round(primaryHeight * 100) / 100;
  const secondaryPx = Math.round(secondaryHeight * 100) / 100;
  const tertiaryPx =
    typeof tertiaryHeight === "number"
      ? Math.round(tertiaryHeight * 100) / 100
      : null;

  let gapAfterPrimary = null;
  let gapAfterSecondary = null;
  let interRowGaps = null;
  let brandGap = null;
  let interRowGap = null;

  if (tertiaryPx !== null) {
    const primaryMax = logoMaxHeights?.primaryMax ?? primaryHeight;
    const secondaryMax = logoMaxHeights?.secondaryMax ?? secondaryHeight;
    const gaps = mobileLogoGaps(
      primaryHeight,
      secondaryHeight,
      primaryMax,
      secondaryMax,
      rowGap
    );
    gapAfterPrimary = gaps.gapAfterPrimary;
    gapAfterSecondary = gaps.gapAfterSecondary;
    interRowGaps = gaps.interRowGapsTotal;
    brandGap = rowGap;
  } else if (logoMaxHeights) {
    const gaps = desktopLogoGaps(
      primaryHeight,
      secondaryHeight,
      logoMaxHeights.primaryMax,
      logoMaxHeights.secondaryMax,
      rowGap,
      stickyMode
    );
    interRowGap = gaps.interRowGap;
    interRowGaps = gaps.interRowGap;
    brandGap = gaps.brandGap;
  }

  const nextKey = logoCssKey([
    primaryPx,
    secondaryPx,
    tertiaryPx,
    gapAfterPrimary,
    gapAfterSecondary,
    interRowGaps,
    brandGap,
    interRowGap,
    Math.round(primaryProgress * 1000) / 1000,
    Math.round(secondaryProgress * 1000) / 1000,
    Math.round(scrollProgress * 1000) / 1000,
  ]);

  if (nextKey === lastLogoCssKey) {
    return;
  }

  lastLogoCssKey = nextKey;

  root.style.setProperty("--ma-logo-row-1-height", `${primaryPx}px`);
  root.style.setProperty("--ma-logo-row-2-height", `${secondaryPx}px`);

  if (tertiaryPx !== null) {
    root.style.setProperty("--ma-logo-row-3-height", `${tertiaryPx}px`);
    root.style.setProperty(
      "--ma-logo-gap-after-primary",
      `${gapAfterPrimary}px`
    );
    root.style.setProperty(
      "--ma-logo-gap-after-secondary",
      `${gapAfterSecondary}px`
    );
    root.style.setProperty("--ma-logo-inter-row-gaps", `${interRowGaps}px`);
    root.style.setProperty("--ma-logo-brand-gap", `${brandGap}px`);
  } else if (logoMaxHeights) {
    root.style.setProperty("--ma-logo-gap", `${interRowGap}px`);
    root.style.setProperty("--ma-logo-inter-row-gaps", `${interRowGaps}px`);
    root.style.setProperty("--ma-logo-brand-gap", `${brandGap}px`);
  }

  root.style.setProperty(
    "--ma-logo-row-primary-progress",
    String(primaryProgress)
  );
  root.style.setProperty(
    "--ma-logo-row-secondary-progress",
    String(secondaryProgress)
  );
  root.style.setProperty("--ma-logo-scroll-progress", String(scrollProgress));
}

function resetMaLogoHeights() {
  lastLogoCssKey = "";
  const root = document.documentElement;
  root.style.removeProperty("--ma-logo-row-1-height");
  root.style.removeProperty("--ma-logo-row-2-height");
  root.style.removeProperty("--ma-logo-row-3-height");
  root.style.removeProperty("--ma-logo-gap");
  root.style.removeProperty("--ma-logo-gap-after-primary");
  root.style.removeProperty("--ma-logo-gap-after-secondary");
  root.style.removeProperty("--ma-logo-inter-row-gaps");
  root.style.removeProperty("--ma-logo-brand-gap");
  root.style.removeProperty("--ma-logo-row-primary-progress");
  root.style.removeProperty("--ma-logo-row-secondary-progress");
  root.style.removeProperty("--ma-logo-scroll-progress");
}

function measureLogoGap(logo) {
  const styles = getComputedStyle(logo);
  const gap = parseFloat(styles.rowGap || styles.gap || "0");
  return Number.isFinite(gap) ? gap : 0;
}

function measureLogoHeights(logo) {
  const root = document.documentElement;
  // Intro CSS overrides resting row vars — strip it while measuring true rest size.
  const hadIntro = root.classList.contains("is-ma-logo-intro");
  if (hadIntro) {
    root.classList.remove("is-ma-logo-intro");
  }

  resetMaLogoHeights();

  const primary = logo.querySelector(".c-ma-logo__row--primary");
  const secondary = logo.querySelector(".c-ma-logo__row--secondary");

  if (!primary || !secondary) {
    if (hadIntro) {
      root.classList.add("is-ma-logo-intro");
    }
    return null;
  }

  // Force layout with resting CSS vars before reading bounds.
  void logo.offsetHeight;

  const primaryMax = primary.getBoundingClientRect().height;
  const secondaryMax = secondary.getBoundingClientRect().height;
  const tertiaryMax = isMobileLogoLayout(logo)
    ? tertiaryHeightFromSecondary(secondaryMax)
    : 0;
  const primaryPhaseRange = phaseScrollRange(primaryMax);
  const secondaryPhaseRange = phaseScrollRange(secondaryMax);
  const stickyMode = getLogoStickyMode();
  const mobileThreeRow = tertiaryMax > 0;
  // Mobile: collapse primary then secondary; tertiary stays as sticky rest.
  // Desktop keep-first-MA: only primary scrolls; secondary stays.
  const totalScrollRange = mobileThreeRow
    ? primaryPhaseRange + secondaryPhaseRange
    : keepsFirstMa(stickyMode)
      ? primaryPhaseRange
      : primaryPhaseRange + secondaryPhaseRange;
  const gap = measureLogoGap(logo);

  if (hadIntro) {
    root.classList.add("is-ma-logo-intro");
  }

  return {
    primaryMax,
    secondaryMax,
    tertiaryMax,
    primaryPhaseRange,
    secondaryPhaseRange,
    totalScrollRange,
    gap,
    stickyMode,
  };
}

function mapScrollProgress(rawProgress, smooth) {
  const clamped = clamp(rawProgress, 0, 1);
  return smooth ? easeInOutCubic(clamped) : clamped;
}

/**
 * Nearest open/closed scrollY only when near a phase edge.
 * Mid-phase (and outside the collapse band) returns scrollY unchanged.
 */
function snappedPhaseScrollY(scrollY, heights) {
  const {
    primaryPhaseRange,
    secondaryPhaseRange,
    totalScrollRange,
    tertiaryMax = 0,
    stickyMode = getLogoStickyMode(),
  } = heights;

  const y = Math.max(0, scrollY);

  if (y <= 0 || y >= totalScrollRange) {
    return y;
  }

  const snapPhaseProgress = (progress, phaseStart, phaseEnd) => {
    if (progress <= PHASE_SNAP_OPEN_MAX) {
      return phaseStart;
    }

    if (progress >= PHASE_SNAP_CLOSE_MIN) {
      return phaseEnd;
    }

    return y;
  };

  const dualPhase = tertiaryMax > 0 || !keepsFirstMa(stickyMode);

  if (!dualPhase || y <= primaryPhaseRange) {
    const progress =
      primaryPhaseRange > 0 ? clamp(y / primaryPhaseRange, 0, 1) : 1;
    return snapPhaseProgress(progress, 0, primaryPhaseRange);
  }

  const secondaryScroll = y - primaryPhaseRange;
  const progress =
    secondaryPhaseRange > 0
      ? clamp(secondaryScroll / secondaryPhaseRange, 0, 1)
      : 1;

  return snapPhaseProgress(
    progress,
    primaryPhaseRange,
    primaryPhaseRange + secondaryPhaseRange
  );
}

function computeLogoState(scrollY, heights, overscrollPx = 0, smooth = true) {
  const {
    primaryMax,
    secondaryMax,
    tertiaryMax = 0,
    primaryPhaseRange,
    secondaryPhaseRange,
    totalScrollRange,
    stickyMode = getLogoStickyMode(),
  } = heights;

  const collapseScrollY = Math.max(0, scrollY);
  const keepFirst = keepsFirstMa(stickyMode);
  const mobileThreeRow = tertiaryMax > 0;
  let state;

  if (mobileThreeRow) {
    if (collapseScrollY <= primaryPhaseRange) {
      const rawPrimaryProgress =
        primaryPhaseRange > 0
          ? clamp(collapseScrollY / primaryPhaseRange, 0, 1)
          : 1;
      const primaryProgress = mapScrollProgress(rawPrimaryProgress, smooth);
      const scrollProgress =
        totalScrollRange > 0
          ? clamp(collapseScrollY / totalScrollRange, 0, 1)
          : 0;

      state = {
        primaryHeight: heightAtProgress(primaryMax, primaryProgress),
        secondaryHeight: secondaryMax,
        tertiaryHeight: tertiaryMax,
        primaryProgress,
        secondaryProgress: 0,
        scrollProgress,
      };
    } else {
      const secondaryScroll = collapseScrollY - primaryPhaseRange;
      const rawSecondaryProgress =
        secondaryPhaseRange > 0
          ? clamp(secondaryScroll / secondaryPhaseRange, 0, 1)
          : 1;
      const secondaryProgress = mapScrollProgress(rawSecondaryProgress, smooth);
      const scrollProgress =
        totalScrollRange > 0
          ? clamp(collapseScrollY / totalScrollRange, 0, 1)
          : 1;

      state = {
        primaryHeight: 0,
        secondaryHeight: heightAtProgress(secondaryMax, secondaryProgress),
        tertiaryHeight: tertiaryMax,
        primaryProgress: 1,
        secondaryProgress,
        scrollProgress,
      };
    }
  } else if (keepFirst || collapseScrollY <= primaryPhaseRange) {
    const rawPrimaryProgress =
      primaryPhaseRange > 0
        ? clamp(collapseScrollY / primaryPhaseRange, 0, 1)
        : 1;
    const primaryProgress = mapScrollProgress(rawPrimaryProgress, smooth);
    const primaryHeight = heightAtProgress(primaryMax, primaryProgress);
    const scrollProgress =
      totalScrollRange > 0
        ? clamp(collapseScrollY / totalScrollRange, 0, 1)
        : 0;

    state = {
      primaryHeight,
      // Keep-first-MA: shorter/lower secondary row stays at sticky size.
      secondaryHeight: secondaryMax,
      primaryProgress,
      secondaryProgress: 0,
      scrollProgress,
    };
  } else {
    const secondaryScroll = collapseScrollY - primaryPhaseRange;
    const rawSecondaryProgress =
      secondaryPhaseRange > 0
        ? clamp(secondaryScroll / secondaryPhaseRange, 0, 1)
        : 1;
    const secondaryProgress = mapScrollProgress(rawSecondaryProgress, smooth);
    const scrollProgress =
      totalScrollRange > 0
        ? clamp(collapseScrollY / totalScrollRange, 0, 1)
        : 1;

    state = {
      primaryHeight: 0,
      secondaryHeight: heightAtProgress(secondaryMax, secondaryProgress),
      primaryProgress: 1,
      secondaryProgress,
      scrollProgress,
    };
  }

  if (collapseScrollY === 0 && overscrollPx > 0) {
    return applyOverscrollGrowth(
      state,
      heights,
      overscrollGrowFactor(overscrollPx)
    );
  }

  return state;
}

function restingLogoState(heights) {
  const state = {
    primaryHeight: heights.primaryMax,
    secondaryHeight: heights.secondaryMax,
    primaryProgress: 0,
    secondaryProgress: 0,
    scrollProgress: 0,
  };

  if (heights.tertiaryMax > 0) {
    state.tertiaryHeight = heights.tertiaryMax;
  }

  return state;
}

function markIntroCompleted() {
  document.documentElement.classList.remove("is-ma-logo-intro");
  document.documentElement.classList.add("ma-logo-intro-done");
  persistHomeIntroDone();
  document.dispatchEvent(new CustomEvent(HOME_INTRO_DONE_EVENT));
}

/**
 * Apply settled intro classes when the intro already ran this page life
 * (e.g. Swup return to home before initMaLogoScroll runs).
 */
export function syncHomeIntroClasses() {
  rememberInitialEntryPage();

  if (!document.body.classList.contains("page-home")) {
    return;
  }

  if (hasHomeIntroBeenDone() || prefersReducedMotion()) {
    markIntroCompleted();
  }
}

function shouldRunHomeIntro() {
  rememberInitialEntryPage();

  if (!document.body.classList.contains("page-home")) {
    return false;
  }

  if (prefersReducedMotion()) {
    return false;
  }

  // Soft nav return, or site entry was not the homepage: skip intro.
  if (hasHomeIntroBeenDone()) {
    return false;
  }

  if (window.scrollY > 0) {
    return false;
  }

  return true;
}

function getIntroViewportHeight() {
  const visual = window.visualViewport?.height;
  if (typeof visual === "number" && visual > 0) {
    return visual;
  }

  return window.innerHeight;
}

/**
 * Non-row chrome during homepage intro: header padding + inter-row gaps.
 * Brand/nav gap is collapsed to 0 while `is-ma-logo-intro` is on.
 */
function getIntroChromePx(header, heights) {
  const styles = getComputedStyle(header || document.documentElement);
  const padTop = parseFloat(styles.paddingTop || "0");
  const padBottom = parseFloat(styles.paddingBottom || "0");
  const padY =
    (Number.isFinite(padTop) ? padTop : 0) +
    (Number.isFinite(padBottom) ? padBottom : 0);
  const rowGap = getLogoRowGap();
  // Mobile 3-row uses margin-bottom gaps (logo gap is 0); desktop uses one grid gap.
  const interRowGaps =
    heights.tertiaryMax > 0 ? rowGap * 2 : Math.max(heights.gap || 0, rowGap);

  return padY + interRowGaps;
}

/**
 * Scale resting row heights so rows + intro chrome fill the viewport exactly.
 */
function scaleHeightsToViewport(heights, header) {
  const restRows =
    heights.primaryMax + heights.secondaryMax + (heights.tertiaryMax || 0);

  if (restRows <= 0) {
    return {
      primaryHeight: heights.primaryMax,
      secondaryHeight: heights.secondaryMax,
      tertiaryHeight: heights.tertiaryMax || 0,
    };
  }

  const chrome = getIntroChromePx(header, heights);
  const available = Math.max(0, getIntroViewportHeight() - chrome);
  const rowsTarget = Math.max(restRows, available);
  const factor = rowsTarget / restRows;
  const primaryHeight = heights.primaryMax * factor;
  const secondaryHeight = heights.secondaryMax * factor;

  return {
    primaryHeight,
    secondaryHeight,
    // Keep the 80/185 step exact off the scaled secondary (avoids Safari drift).
    tertiaryHeight:
      heights.tertiaryMax > 0 ? secondaryHeight * LOGO_ROW_STEP : 0,
  };
}

function finishHomeAtRest(logo, header) {
  const heights = measureLogoHeights(logo);

  if (heights) {
    setMaLogoState(withLogoMaxHeights(restingLogoState(heights), heights));
  }

  markIntroCompleted();
}

function runHomeLogoIntro(logo, header) {
  const heights = measureLogoHeights(logo);

  if (!heights) {
    markIntroCompleted();
    return () => {};
  }

  const rest = restingLogoState(heights);
  const start = scaleHeightsToViewport(heights, header);

  document.documentElement.classList.add("is-ma-logo-intro");
  document.documentElement.classList.remove("ma-logo-intro-done");

  const applyIntroHeights = (primaryHeight, secondaryHeight, tertiaryHeight) => {
    setMaLogoState(
      withLogoMaxHeights(
        {
          ...rest,
          primaryHeight,
          secondaryHeight,
          ...(heights.tertiaryMax > 0 ? { tertiaryHeight } : {}),
        },
        heights
      )
    );

    // Intro: keep full row gaps and zero brand gap (nav is hidden).
    if (heights.tertiaryMax > 0) {
      const root = document.documentElement;
      const rowGap = getLogoRowGap();
      root.style.setProperty("--ma-logo-gap-after-primary", `${rowGap}px`);
      root.style.setProperty("--ma-logo-gap-after-secondary", `${rowGap}px`);
      root.style.setProperty("--ma-logo-inter-row-gaps", `${rowGap * 2}px`);
      root.style.setProperty("--ma-logo-brand-gap", "0px");
    }
  };

  applyIntroHeights(
    start.primaryHeight,
    start.secondaryHeight,
    start.tertiaryHeight
  );

  let rafId = null;
  let startTime = null;
  let cancelled = false;

  const onSkip = () => {
    settle(true);
  };

  // Swallow wheel so accidental scroll doesn't move the page mid-intro.
  // Do not treat wheel/scroll as skip — intro should play through.
  const onWheel = (event) => {
    event.preventDefault();
  };

  const detachSkipListeners = () => {
    window.removeEventListener("touchstart", onSkip);
    window.removeEventListener("keydown", onSkip);
    window.removeEventListener("wheel", onWheel);
  };

  const settle = (markDone = true) => {
    if (cancelled) {
      return;
    }

    cancelled = true;
    detachSkipListeners();

    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    setMaLogoState(withLogoMaxHeights(rest, heights));

    if (markDone) {
      markIntroCompleted();
    }
  };

  const tick = (now) => {
    if (cancelled) {
      return;
    }

    if (startTime === null) {
      startTime = now;
    }

    const elapsed = now - startTime;

    // Hold full-viewport logo before scale-down; nav stays hidden until settle.
    if (elapsed < INTRO_HOLD_MS) {
      applyIntroHeights(
        start.primaryHeight,
        start.secondaryHeight,
        start.tertiaryHeight
      );
      rafId = requestAnimationFrame(tick);
      return;
    }

    const t = clamp((elapsed - INTRO_HOLD_MS) / INTRO_DURATION_MS, 0, 1);
    const eased = easeInOutCubic(t);
    const primaryHeight =
      start.primaryHeight + (rest.primaryHeight - start.primaryHeight) * eased;
    const secondaryHeight =
      start.secondaryHeight +
      (rest.secondaryHeight - start.secondaryHeight) * eased;
    const tertiaryHeight =
      heights.tertiaryMax > 0
        ? start.tertiaryHeight +
          ((rest.tertiaryHeight ?? heights.tertiaryMax) - start.tertiaryHeight) *
            eased
        : 0;

    applyIntroHeights(primaryHeight, secondaryHeight, tertiaryHeight);

    if (t < 1) {
      rafId = requestAnimationFrame(tick);
      return;
    }

    settle(true);
  };

  // Touch / key can still skip; wheel is ignored (and blocked) so the intro plays out.
  window.addEventListener("wheel", onWheel, { passive: false });
  window.addEventListener("touchstart", onSkip, { passive: true });
  window.addEventListener("keydown", onSkip, { passive: true });

  rafId = requestAnimationFrame(tick);

  return () => {
    // Navigating away mid-intro: stop motion and hand off to resting state.
    settle(true);
  };
}

function bindLogoScroll(logo, heights) {
  const useSmoothScroll =
    document.body.dataset.logoScrollSmoothing !== "false";

  let currentHeights = heights;
  let displayPrimary = currentHeights.primaryMax;
  let displaySecondary = currentHeights.secondaryMax;
  let displayTertiary = currentHeights.tertiaryMax || 0;
  let rafId = null;
  let pullDelta = 0;
  let touchStartY = null;
  let touchTracking = false;
  // Contact (and similar overlays) hold the logo at resting height.
  let holdExpanded = false;
  let phaseSnapTimer = null;
  let applyingPhaseSnap = false;

  const getOverscrollPx = () => {
    const fromScroll = Math.max(0, -window.scrollY);
    return clamp(Math.max(fromScroll, pullDelta), 0, OVERSCROLL_MAX);
  };

  const decayPullDelta = () => {
    if (holdExpanded || window.scrollY > 0) {
      pullDelta = 0;
      return;
    }

    if (pullDelta > 0.5) {
      pullDelta *= PULL_DECAY;
      return;
    }

    pullDelta = 0;
  };

  const targetLogoState = () => {
    if (holdExpanded) {
      return restingLogoState(currentHeights);
    }

    return computeLogoState(
      window.scrollY,
      currentHeights,
      getOverscrollPx(),
      useSmoothScroll
    );
  };

  const applyState = (state) => {
    setMaLogoState(
      withLogoMaxHeights(
        {
          primaryHeight: displayPrimary,
          secondaryHeight: displaySecondary,
          tertiaryHeight:
            displayTertiary > 0 ? displayTertiary : state.tertiaryHeight,
          primaryProgress: state.primaryProgress,
          secondaryProgress: state.secondaryProgress,
          scrollProgress: state.scrollProgress,
        },
        currentHeights
      )
    );
  };

  const snapToTarget = () => {
    const state = targetLogoState();
    displayPrimary = state.primaryHeight;
    displaySecondary = state.secondaryHeight;
    displayTertiary = state.tertiaryHeight ?? displayTertiary;
    setMaLogoState(withLogoMaxHeights(state, currentHeights));
  };

  const clearPhaseSnapTimer = () => {
    if (phaseSnapTimer !== null) {
      clearTimeout(phaseSnapTimer);
      phaseSnapTimer = null;
    }
  };

  const applyPhaseSnap = () => {
    if (!currentHeights || holdExpanded) {
      return;
    }

    const current = window.scrollY;
    const snapped = snappedPhaseScrollY(current, currentHeights);

    if (Math.abs(snapped - current) < 0.5) {
      return;
    }

    applyingPhaseSnap = true;
    window.scrollTo({ top: snapped, left: 0, behavior: "auto" });
    applyingPhaseSnap = false;

    if (useSmoothScroll) {
      scheduleSmoothTick();
      return;
    }

    snapToTarget();
  };

  const schedulePhaseSnap = () => {
    clearPhaseSnapTimer();

    if (holdExpanded) {
      return;
    }

    phaseSnapTimer = setTimeout(() => {
      phaseSnapTimer = null;
      applyPhaseSnap();
    }, PHASE_SNAP_IDLE_MS);
  };

  const tickSmooth = () => {
    if (!currentHeights) {
      rafId = null;
      return;
    }

    decayPullDelta();

    const target = targetLogoState();
    displayPrimary = smoothToward(
      displayPrimary,
      target.primaryHeight,
      LERP_FACTOR
    );
    displaySecondary = smoothToward(
      displaySecondary,
      target.secondaryHeight,
      LERP_FACTOR
    );

    if (displayTertiary > 0 || target.tertiaryHeight != null) {
      displayTertiary = smoothToward(
        displayTertiary,
        target.tertiaryHeight ?? displayTertiary,
        LERP_FACTOR
      );
    }

    applyState(target);

    const tertiarySettled =
      displayTertiary <= 0 ||
      target.tertiaryHeight == null ||
      Math.abs(displayTertiary - target.tertiaryHeight) < SETTLE_THRESHOLD;
    const settled =
      Math.abs(displayPrimary - target.primaryHeight) < SETTLE_THRESHOLD &&
      Math.abs(displaySecondary - target.secondaryHeight) < SETTLE_THRESHOLD &&
      tertiarySettled &&
      (holdExpanded || pullDelta < 0.5);

    if (settled) {
      displayPrimary = target.primaryHeight;
      displaySecondary = target.secondaryHeight;
      displayTertiary = target.tertiaryHeight ?? displayTertiary;
      setMaLogoState(withLogoMaxHeights(target, currentHeights));
      rafId = null;
      return;
    }

    rafId = requestAnimationFrame(tickSmooth);
  };

  const tickDirectDecay = () => {
    if (!currentHeights || holdExpanded) {
      rafId = null;
      return;
    }

    decayPullDelta();
    snapToTarget();

    if (pullDelta > 0.5) {
      rafId = requestAnimationFrame(tickDirectDecay);
      return;
    }

    rafId = null;
  };

  const scheduleSmoothTick = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(tickSmooth);
    }
  };

  const scheduleDirectDecay = () => {
    if (holdExpanded) {
      snapToTarget();
      return;
    }

    snapToTarget();

    if (pullDelta > 0.5 && rafId === null) {
      rafId = requestAnimationFrame(tickDirectDecay);
    }
  };

  const onResize = () => {
    resetLogoRowGapCache();
    currentHeights = measureLogoHeights(logo);
    if (!currentHeights) {
      return;
    }

    if (holdExpanded) {
      snapToTarget();
      return;
    }

    snapToTarget();
    schedulePhaseSnap();
  };

  const onScroll = () => {
    if (holdExpanded) {
      return;
    }

    // Past the logo collapse range with display already settled: skip work.
    // Prevents rewriting :root CSS vars (sticky header height) on every
    // scroll frame of long image-heavy pages (e.g. project).
    if (
      currentHeights &&
      window.scrollY >= currentHeights.totalScrollRange &&
      pullDelta < 0.5
    ) {
      const collapsed = computeLogoState(
        currentHeights.totalScrollRange,
        currentHeights,
        0,
        false
      );
      const tertiaryTarget = collapsed.tertiaryHeight ?? displayTertiary;
      const settled =
        Math.abs(displayPrimary - collapsed.primaryHeight) < SETTLE_THRESHOLD &&
        Math.abs(displaySecondary - collapsed.secondaryHeight) <
          SETTLE_THRESHOLD &&
        Math.abs(displayTertiary - tertiaryTarget) < SETTLE_THRESHOLD;

      if (settled) {
        clearPhaseSnapTimer();
        return;
      }
    }

    if (
      !applyingPhaseSnap &&
      currentHeights &&
      window.scrollY < currentHeights.totalScrollRange
    ) {
      schedulePhaseSnap();
    }

    if (useSmoothScroll) {
      scheduleSmoothTick();
      return;
    }

    snapToTarget();
  };

  const onWheel = (event) => {
    if (holdExpanded || window.scrollY > 0 || event.deltaY >= 0) {
      return;
    }

    pullDelta = clamp(pullDelta - event.deltaY * 0.6, 0, OVERSCROLL_MAX);

    if (useSmoothScroll) {
      scheduleSmoothTick();
      return;
    }

    scheduleDirectDecay();
  };

  const onTouchStart = (event) => {
    if (holdExpanded || window.scrollY > 0 || event.touches.length !== 1) {
      touchTracking = false;
      touchStartY = null;
      return;
    }

    touchStartY = event.touches[0].clientY;
    touchTracking = true;
  };

  const onTouchMove = (event) => {
    if (holdExpanded || !touchTracking || touchStartY === null) {
      return;
    }

    const delta = event.touches[0].clientY - touchStartY;

    if (delta > 0) {
      pullDelta = clamp(delta, 0, OVERSCROLL_MAX);

      if (useSmoothScroll) {
        scheduleSmoothTick();
      } else {
        scheduleDirectDecay();
      }
    } else if (pullDelta > 0) {
      pullDelta = 0;

      if (useSmoothScroll) {
        scheduleSmoothTick();
      } else {
        scheduleDirectDecay();
      }
    }
  };

  const onTouchEnd = () => {
    touchStartY = null;
    touchTracking = false;

    if (holdExpanded) {
      return;
    }

    schedulePhaseSnap();

    if (useSmoothScroll) {
      scheduleSmoothTick();
    } else {
      scheduleDirectDecay();
    }
  };

  const setExpanded = (expanded) => {
    const next = Boolean(expanded);

    if (holdExpanded === next) {
      if (next) {
        // Re-assert rest in case heights changed while held open.
        if (useSmoothScroll) {
          scheduleSmoothTick();
        } else {
          snapToTarget();
        }
      }
      return;
    }

    holdExpanded = next;
    pullDelta = 0;
    touchTracking = false;
    touchStartY = null;
    clearPhaseSnapTimer();

    if (useSmoothScroll) {
      scheduleSmoothTick();
      return;
    }

    snapToTarget();
  };

  snapToTarget();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("wheel", onWheel, { passive: true });
  window.addEventListener("touchstart", onTouchStart, { passive: true });
  window.addEventListener("touchmove", onTouchMove, { passive: true });
  window.addEventListener("touchend", onTouchEnd, { passive: true });
  window.addEventListener("touchcancel", onTouchEnd, { passive: true });

  const cleanup = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("wheel", onWheel);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", onTouchEnd);
    window.removeEventListener("touchcancel", onTouchEnd);
    clearPhaseSnapTimer();
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    holdExpanded = false;
    resetMaLogoHeights();
  };

  return { cleanup, setExpanded };
}

/** Active logo controller — scroll-bound pages can hold/reveal at rest. */
let logoController = null;

/**
 * Expand the MA logo to its resting (full) row heights, or release back to
 * scroll-driven collapse. Used while Contact is open so a scrolled/collapsed
 * logo reveals again via the same `--ma-logo-row-*` CSS vars.
 * In keep-first-MA mode the scroll-collapsed rest is the secondary row height,
 * not zero — releasing from expanded still resumes that mode.
 */
export function setLogoExpanded(expanded) {
  logoController?.setExpanded?.(Boolean(expanded));
}

function createRestingLogoController(logo) {
  return {
    setExpanded(expanded) {
      if (!expanded) {
        return;
      }

      const heights = measureLogoHeights(logo);
      if (heights) {
        setMaLogoState(withLogoMaxHeights(restingLogoState(heights), heights));
      }
    },
  };
}

export function initMaLogoScroll() {
  rememberInitialEntryPage();

  const logo = document.querySelector(".js-ma-logo");
  const header = document.getElementById("header");

  if (!logo) {
    logoController = null;
    return;
  }

  if (window.main?.maLogoScrollCleanup) {
    window.main.maLogoScrollCleanup();
  }

  logoController = null;

  const isHomePage = document.body.classList.contains("page-home");
  let introCleanup = null;

  if (prefersReducedMotion()) {
    const heights = measureLogoHeights(logo);
    if (heights) {
      setMaLogoState(withLogoMaxHeights(restingLogoState(heights), heights));
    }
    markIntroCompleted();
    logoController = createRestingLogoController(logo);
    window.main = window.main || {};
    window.main.maLogoScrollCleanup = () => {
      logoController = null;
      resetMaLogoHeights();
    };
    return;
  }

  // Homepage: intro on hard load/refresh; soft-nav returns stay settled.
  if (isHomePage) {
    if (shouldRunHomeIntro()) {
      introCleanup = runHomeLogoIntro(logo, header);
    } else {
      finishHomeAtRest(logo, header);
    }

    logoController = createRestingLogoController(logo);
    window.main = window.main || {};
    window.main.maLogoScrollCleanup = () => {
      if (introCleanup) {
        introCleanup();
        introCleanup = null;
      }
      logoController = null;
      resetMaLogoHeights();
    };
    return;
  }

  // Non-home: ensure nav is visible if we arrived from a home intro state.
  document.documentElement.classList.remove("is-ma-logo-intro");

  const heights = measureLogoHeights(logo);

  if (!heights) {
    return;
  }

  const scrollBinding = bindLogoScroll(logo, heights);
  logoController = scrollBinding;

  // Page transition may re-init while Contact or mobile menu is still open.
  if (
    document.querySelector(".js-header-contact.is-open") ||
    document.querySelector(".js-primary-nav.is-open")
  ) {
    scrollBinding.setExpanded(true);
  }

  window.main = window.main || {};
  window.main.maLogoScrollCleanup = () => {
    scrollBinding.cleanup();
    logoController = null;
  };
}
